import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import {
  cn,
  resolveAnchorId,
  resolveBackgroundImageStyle,
  type BackgroundImageProps,
} from '@/hen-ui/utils';
import { sectionVariants, type SectionVariantProps } from './section.variants';

export interface SectionProps
  extends React.HTMLAttributes<HTMLElement>,
    SectionVariantProps,
    BackgroundImageProps {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
  /** Use semantic section element */
  as?: 'section' | 'div';
  /**
   * In-page jump target. Rendered as the root element's `id`.
   * A leading `#` is ignored. Blank values are omitted.
   * When both this and `id` are set, this wins.
   */
  anchorId?: string;
}

/**
 * Section - Container component with background, padding, and spacing
 *
 * Replaces manual `<section>` tags and `<div>` containers with background colors,
 * padding, and rounded corners. Use Section for major page divisions.
 *
 * Optional CSS background-image props (`backgroundImage`, `backgroundSize`,
 * `backgroundRepeat`, `backgroundPosition`) paint on the root. Prefer
 * `variant="default"` (no solid fill) when the photo should dominate; ensure
 * text contrast (Storybook shows a readable treatment — no built-in overlay).
 *
 * @example Basic usage
 * ```tsx
 * <Section variant="light" gap="3xl" rounded>
 *   <Heading variant="h2">Section Title</Heading>
 *   <Text variant="b3">Section content goes here.</Text>
 * </Section>
 * ```
 *
 * @example Dark section
 * ```tsx
 * <Section variant="dark" gap="xl">
 *   <Heading variant="h2" color="white">Dark Section</Heading>
 *   <Text variant="b3" color="white">Content on dark background.</Text>
 * </Section>
 * ```
 *
 * @example Outlined section
 * ```tsx
 * <Section variant="white-outline" rounded>
 *   <Heading variant="h3">Card-like Section</Heading>
 *   <Text variant="b4">Content with border.</Text>
 * </Section>
 * ```
 *
 * @example Background image
 * ```tsx
 * <Section
 *   variant="default"
 *   backgroundImage="https://cdn.example.com/partner-band.jpg"
 *   rounded="xl"
 *   gap="3xl"
 * >
 *   <Heading variant="h2" color="white">Partner band</Heading>
 * </Section>
 * ```
 */
export const Section = React.forwardRef<HTMLElement, SectionProps>(
  (
    {
      className,
      variant = 'default',
      gap = '3xl',
      padding = 'section',
      rounded = false,
      minHeight,
      position,
      asChild = false,
      as = 'section',
      children,
      anchorId,
      id,
      backgroundImage,
      backgroundSize,
      backgroundRepeat,
      backgroundPosition,
      style,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : as;
    const backgroundStyle = resolveBackgroundImageStyle({
      backgroundImage,
      backgroundSize,
      backgroundRepeat,
      backgroundPosition,
    });
    const mergedStyle =
      backgroundStyle || style ? { ...backgroundStyle, ...style } : undefined;
    const anchor = resolveAnchorId(anchorId, id);

    return (
      <Comp
        className={cn(
          sectionVariants({
            variant,
            gap,
            padding,
            rounded,
            minHeight,
            position,
          }),
          className,
        )}
        // Slot/`div` typings expect HTMLDivElement; `as="section"` is HTMLElement.
        ref={ref as React.Ref<HTMLDivElement>}
        style={mergedStyle}
        {...props}
        {...(anchor ? { id: anchor } : {})}
      >
        {children}
      </Comp>
    );
  },
);

Section.displayName = 'Section';
